import { computed, inject, Injectable, resource } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ApiService } from '@re-core/ngx-ui';
import { toSignal } from '@angular/core/rxjs-interop';
import { MarketingOrderService } from '@lc/core/lib/services/marketing-order.service';
import { ProductCode } from '@lc/core/lib/models/product.code';
import { AppService } from '@lc/core/lib/services/app.service';
import { MarketingOrder } from '@lc/core/lib/models/marketing-order.model';

export interface PropertyWebsiteConfig {
  propertyId: string;
  marketingOrderId: string;
  published: boolean;
  publicUrl: string;
  unbrandedUrl: string;
  previewUrl: string;
  archiveUrl: string;
}

/**
 * Service to share property website state between website-editor.view and print-preview components
 */
@Injectable({
  providedIn: 'root',
})
export class PropertyWebsiteService {
  private apiService = inject(ApiService);
  readonly marketingOrderService = inject(MarketingOrderService);
  readonly marketingOrder = toSignal(this.marketingOrderService.getCurrentOrder$());
  readonly product = computed(() => this.marketingOrder().getProduct(ProductCode.PROPERTY_WEBSITE));

  readonly websiteResource = resource({
    params: () => ({
      order: this.marketingOrder(),
    }),
    loader: async ({ params }) => {
      const url = `${AppService.get('propertyWebsiteApiBaseURL')}websites/${params.order._id}`;
      const config = await firstValueFrom(this.apiService.getOne$<PropertyWebsiteConfig>(url));
      return config;
    },
    defaultValue: null,
  });
  readonly propertyWebsiteConfig = this.websiteResource.value.asReadonly();
  readonly published = computed(() => this.propertyWebsiteConfig()?.published);
  readonly url = computed(() => this.propertyWebsiteConfig()?.publicUrl);
  readonly unbrandedUrl = computed(() => this.propertyWebsiteConfig()?.unbrandedUrl);
  readonly previewUrl = computed(() => this.propertyWebsiteConfig()?.previewUrl);
  readonly archivedUrl = computed(() => this.propertyWebsiteConfig()?.archiveUrl);

  readonly apiUrl = AppService.get('propertyWebsiteApiBaseURL');

  async updatePublishedUri(marketingOrderId: string, uris: string[]) {
    const order = await this.apiService.post<MarketingOrder, Array<{ uri: string }>>(
      `${AppService.get('persistBaseURL')}/marketing-orders/${marketingOrderId}/products/${
        ProductCode.PROPERTY_WEBSITE
      }/publishedUris`,
      uris.map((uri) => ({ uri })),
    );
    await this.marketingOrderService.updateOrder(order);
  }

  async publishPropertyWebsite(marketingOrderId: string): Promise<void> {
    try {
      const url = `${this.apiUrl}websites/${marketingOrderId}/publish`;
      const config = await this.apiService.post<PropertyWebsiteConfig>(url, null);

      if (config?.propertyId) {
        this.websiteResource.set(config);
      }

      await this.updatePublishedUri(marketingOrderId, [config.publicUrl]);
    } catch (error) {
      console.info('[PropertyWebsiteStateService] Publish error:', error);
    }
  }

  async unpublishPropertyWebsite(marketingOrderId: string): Promise<void> {
    try {
      const url = `${this.apiUrl}websites/${marketingOrderId}/unpublish`;
      const config = await this.apiService.post<PropertyWebsiteConfig>(url, null);

      if (config?.propertyId) {
        this.websiteResource.set(config);
      }

      await this.updatePublishedUri(marketingOrderId, []);
    } catch (error) {
      console.info('[PropertyWebsiteStateService] Unublish error:', error);
    }
  }
}
